import React, { useState, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import PTTLogo from './assets/images/ptt_logo.svg';

const fetcher = url => {
    const token = localStorage.getItem('token');

    return fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    }).then(res => {
        if (!res.ok) {
            const error = new Error('Yetkilendirme hatası veya sunucuya ulaşılamadı.');
            error.info = res.statusText;
            error.status = res.status;
            throw error;
        }
        return res.json();
    });
};


function BekleyenKargolar() {
    const [seciliSiparisler, setSeciliSiparisler] = useState([]);
    const [manuelKontrolYapiliyor, setManuelKontrolYapiliyor] = useState(false);
    const [sonKontrolTarihi, setSonKontrolTarihi] = useState(null);
    const [kontrolEdilenIds, setKontrolEdilenIds] = useState([]); // Kontrol edilen sipariş ID'leri
    const [kontrolIlerlemesi, setKontrolIlerlemesi] = useState({ current: 0, total: 0 }); // İlerleme takibi
    const [dots, setDots] = useState(''); // Üç nokta animasyonu
    
    const { data: bekleyenler, error } = useSWR('/api/bekleyen-kargolar', fetcher);
    const { data: otomatikKontrol } = useSWR('/api/bekleyen-kargolar/otomatik-kontrol-durumu', fetcher, { refreshInterval: 300000 });
    const sonOtomatik = otomatikKontrol?.son_calisma;

    // Otomatik kontrol saatleri (sunucudaki syp-ptt-kontrol.timer ile aynı olmalı)
    const OTOMATIK_SAATLER = [7, 19];
    const sonrakiOtomatikKontrol = () => {
        const simdi = new Date();
        const sonraki = OTOMATIK_SAATLER.map(saat => {
            const t = new Date(simdi);
            t.setHours(saat, 0, 0, 0);
            if (t <= simdi) t.setDate(t.getDate() + 1);
            return t;
        }).sort((a, b) => a - b)[0];
        const bugunMu = sonraki.toDateString() === simdi.toDateString();
        return `${bugunMu ? 'bugün' : 'yarın'} ${String(sonraki.getHours()).padStart(2, '0')}:00`;
    };

    // Üç nokta animasyonu
    useEffect(() => {
        if (!manuelKontrolYapiliyor) {
            setDots('');
            return;
        }
        
        const interval = setInterval(() => {
            setDots(prev => {
                if (prev === '...') return '.';
                return prev + '.';
            });
        }, 500);
        
        return () => clearInterval(interval);
    }, [manuelKontrolYapiliyor]);

    const handleCikisYap = async (siparisId) => {
        const isConfirmed = window.confirm("Bu sipariş bekleyenler listesinden çıkarılacak. Emin misiniz?");
        if (!isConfirmed) return;

        try {
            const response = await fetch(`/api/siparisler/${siparisId}/durum-guncelle`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ kargo_durumu: 'Normal' })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.hata);
            
            alert(data.mesaj);
            mutate('/api/bekleyen-kargolar');
        } catch (error) {
            alert(`Hata: ${error.message}`);
        }
    };

    // Otomatik PTT kontrol fonksiyonu - Tüm siparişleri 20'şer 20'şer kontrol eder
    const handleManuelPttKontrol = async () => {
        if (!window.confirm('Tüm beklemede olan siparişler PTT\'den otomatik kontrol edilecek (20\'şer 20\'şer). Bu işlem birkaç dakika sürebilir. Devam etmek istiyor musunuz?')) {
            return;
        }

        setManuelKontrolYapiliyor(true);
        setKontrolEdilenIds([]);
        
        let tumKontrolEdilenIds = [];
        let toplamGuncellenen = 0;
        let batch = 0;
        let devamEt = true;
        
        try {
            const token = localStorage.getItem('token');
            
            while (devamEt) {
                batch++;
                
                // Her batch için timeout (2 dakika)
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 120000);
                
                setKontrolIlerlemesi({ current: tumKontrolEdilenIds.length, total: '?' });
                
                const response = await fetch('/api/bekleyen-kargolar/manuel-kontrol', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ 
                        batch_limit: 20,
                        exclude_ids: tumKontrolEdilenIds
                    }),
                    signal: controller.signal
                });
                
                clearTimeout(timeoutId);
                
                const data = await response.json();
                
                if (!response.ok) {
                    throw new Error(data.hata || data.detay || 'Kontrol sırasında hata oluştu');
                }
                
                // İstatistikleri güncelle
                toplamGuncellenen += data.guncellenen_sayisi;
                
                if (data.checked_ids && data.checked_ids.length > 0) {
                    tumKontrolEdilenIds = [...tumKontrolEdilenIds, ...data.checked_ids];
                    setKontrolEdilenIds(tumKontrolEdilenIds);
                }
                
                // İlerlemeyi güncelle
                const toplamSiparis = data.toplam_kontrol + tumKontrolEdilenIds.length;
                setKontrolIlerlemesi({ 
                    current: tumKontrolEdilenIds.length, 
                    total: toplamSiparis 
                });
                
                // Kalan sipariş yoksa dur
                if (data.kalan === 0 || data.batch_kontrol === 0) {
                    devamEt = false;
                    
                    // Son kontrol tarihini güncelle
                    const now = new Date();
                    setSonKontrolTarihi(now.toLocaleString('tr-TR'));
                    
                    // Sonuç mesajı
                    alert(`✅ PTT Kontrolü Tamamlandı!\n\n📦 Toplam kontrol edilen: ${tumKontrolEdilenIds.length} sipariş\n✅ Güncellenen: ${toplamGuncellenen} sipariş\n🔄 Batch sayısı: ${batch}`);
                    
                    // Listeyi sıfırla
                    setKontrolEdilenIds([]);
                    setKontrolIlerlemesi({ current: 0, total: 0 });
                } else {
                    // Kısa bir bekleme (UI güncellemesi için)
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
            
            mutate('/api/bekleyen-kargolar');
            
        } catch (error) {
            if (error.name === 'AbortError') {
                alert(`⏱️ İşlem zaman aşımına uğradı.\n\n${tumKontrolEdilenIds.length} sipariş kontrol edildi, ${toplamGuncellenen} güncellendi.\nKaldığı yerden devam etmek için tekrar basın.`);
            } else {
                alert(`❌ Hata: ${error.message}\n\nKontrol edilen: ${tumKontrolEdilenIds.length}, Güncellenen: ${toplamGuncellenen}`);
            }
        } finally {
            setManuelKontrolYapiliyor(false);
        }
    };

    // Seçim fonksiyonları
    const handleSecim = (siparisId) => {
        setSeciliSiparisler(prev => {
            if (prev.includes(siparisId)) {
                return prev.filter(id => id !== siparisId);
            } else {
                return [...prev, siparisId];
            }
        });
    };

    const handleTumunuSec = () => {
        if (seciliSiparisler.length === bekleyenler.length) {
            setSeciliSiparisler([]);
        } else {
            setSeciliSiparisler(bekleyenler.map(siparis => siparis.id));
        }
    };

    // Toplu çıkarma fonksiyonu
    const handleTopluCikisYap = async () => {
        if (seciliSiparisler.length === 0) return;
        
        const isConfirmed = window.confirm(`${seciliSiparisler.length} sipariş bekleyenler listesinden çıkarılacak. Emin misiniz?`);
        if (!isConfirmed) return;

        try {
            const promises = seciliSiparisler.map(siparisId => 
                fetch(`/api/siparisler/${siparisId}/durum-guncelle`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ kargo_durumu: 'Normal' })
                })
            );

            await Promise.all(promises);
            alert(`${seciliSiparisler.length} sipariş başarıyla listeden çıkarıldı.`);
            setSeciliSiparisler([]);
            mutate('/api/bekleyen-kargolar');
        } catch (error) {
            alert(`Hata: ${error.message}`);
        }
    };

    if (error) return <div>Veri yüklenirken hata oluştu.</div>;
    if (!bekleyenler) return (
        <div style={{ 
            background: '#F8F8F8', 
            padding: '20px', 
            borderRadius: '8px', 
            height: 'calc(100vh - 110px)', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontFamily: 'Poppins, sans-serif' 
        }}>
            <div style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}>
                ⏳ Yükleniyor...
            </div>
        </div>
    );

    return (
        <div style={{ background: '#F8F8F8', padding: '20px', borderRadius: '8px', height: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
            
            {/* Üst Panel - PTT Kontrol ve Toplu İşlem Butonları */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
                {/* Sol taraf - PTT Kontrol */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            onClick={handleManuelPttKontrol}
                            disabled={manuelKontrolYapiliyor}
                            style={{
                                padding: '8px 16px',
                                background: manuelKontrolYapiliyor ? '#ccc' : '#fff',
                                color: '#333',
                                fontFamily: 'Poppins',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                cursor: manuelKontrolYapiliyor ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s ease',
                                minWidth: '180px'
                            }}
                            onMouseEnter={(e) => {
                                if (!manuelKontrolYapiliyor) {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                            }}
                        >
                            <img src={PTTLogo} alt="PTT" style={{ height: '16px' }} />
                            <span>
                                {manuelKontrolYapiliyor 
                                    ? `Kontrol Ediliyor${dots}` 
                                    : 'Kontrol Et'}
                            </span>
                        </button>
                    </div>
                    {sonKontrolTarihi && !manuelKontrolYapiliyor && (
                        <div style={{ fontSize: '11px', color: '#666', fontFamily: 'Poppins', paddingLeft: '4px' }}>
                            Son kontrol: {sonKontrolTarihi}
                        </div>
                    )}
                    <div style={{ fontSize: '11px', color: '#666', fontFamily: 'Poppins', paddingLeft: '4px' }}>
                        {sonOtomatik ? (
                            <span style={{ color: sonOtomatik.basarili ? '#666' : '#c0392b' }}>
                                Son otomatik kontrol: {new Date(sonOtomatik.bitis || sonOtomatik.baslangic).toLocaleString('tr-TR')}
                                {sonOtomatik.basarili
                                    ? ` · ${sonOtomatik.kontrol_edilen} kontrol, ${sonOtomatik.guncellenen} güncellendi`
                                      + (sonOtomatik.takip_kontrol_edilen ? ` · ${sonOtomatik.takip_kontrol_edilen} gönderinin takip durumu yenilendi` : '')
                                    : ` · hata: ${sonOtomatik.hata || 'bilinmiyor'}`}
                                {sonOtomatik.hatali_batch > 0 && ` · ${sonOtomatik.hatali_batch} grup PTT'den yanıt alamadı`}
                            </span>
                        ) : (
                            <span>Otomatik kontrol henüz çalışmadı</span>
                        )}
                        <span> · Sonraki: {sonrakiOtomatikKontrol()}</span>
                    </div>
                </div>
                
                {/* Sağ taraf - Toplu İşlemler */}
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={handleTopluCikisYap}
                        disabled={seciliSiparisler.length === 0}
                        style={{
                            padding: '5px 12px', 
                            background: 'f8f8f8', 
                            fontFamily: 'poppins', 
                            alignItems: 'center', 
                            display: 'flex', 
                            border: '1px solid #ccc', 
                            borderRadius: '4px', 
                            gap: '8px',
                            opacity: seciliSiparisler.length === 0 ? '0.5' : '1',
                            cursor: seciliSiparisler.length === 0 ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <span>Toplu Listeden Çıkar ({seciliSiparisler.length})</span>
                    </button>
                </div>
            </div>

            <style>{`
                .siparis-satiri {
                    transition: all 0.2s ease-in-out;
                }
                .siparis-satiri:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
                    z-index: 10;
                    position: relative;
                }
                `}</style>

            {bekleyenler.length > 0 ? (
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px' }}>
                    <thead>
                        <tr style={{ background: '#E5E5E5', height: '35px' }}>
                            <th style={{ padding: '12px', width: '30px', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd' }}>
                                <input 
                                    type="checkbox"
                                    onChange={handleTumunuSec}
                                    checked={bekleyenler.length > 0 && seciliSiparisler.length === bekleyenler.length}
                                />
                            </th>
                            <th style={{padding: '12px', fontWeight: '500', textAlign: 'left', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd'}}>Sipariş Tarihi</th>
                            <th style={{padding: '12px', fontWeight: '500', textAlign: 'left', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd'}}>Sipariş No</th>
                            <th style={{padding: '12px', fontWeight: '500', textAlign: 'left', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd'}}>Telefon</th>
                            <th style={{padding: '12px', fontWeight: '500', textAlign: 'left', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd'}}>Ad Soyad</th>
                            <th style={{padding: '12px', fontWeight: '500', textAlign: 'center', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd'}}>İl / İlçe</th>
                            <th style={{padding: '12px', fontWeight: '500', textAlign: 'center', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd'}}>Notlar</th>
                            <th style={{padding: '12px', fontWeight: '500', textAlign: 'center', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd'}}>Tutar</th>
                            <th style={{padding: '12px', fontWeight: '500', textAlign: 'center', position: 'sticky', top: 0, zIndex: 20, background: '#E5E5E5', borderBottom: '1px solid #ddd'}}>İşlem</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bekleyenler.map(siparis => (
                            <tr key={siparis.id} className="siparis-satiri"  style={{ borderBottom: '1px solid #ddd', background: 'white' }}>
                                <td style={{ padding: '12px', textAlign: 'center', width: '30px' }} onClick={(e) => e.stopPropagation()}>
                                    <input 
                                        type="checkbox"
                                        checked={seciliSiparisler.includes(siparis.id)}
                                        onChange={() => handleSecim(siparis.id)}
                                    />
                                </td>
                                <td style={{padding: '12px'}}>{siparis.siparis_tarihi}</td>
                                <td style={{padding: '12px'}}>{siparis.siparis_no}</td>
                                <td style={{padding: '12px'}}>{siparis.telefon_numarasi}</td>
                                <td style={{padding: '12px'}}>{siparis.ad_soyad}</td>
                                <td style={{padding: '12px', textAlign: 'center',}}>{`${siparis.il || ''} / ${siparis.ilce || ''}`}</td>
                                <td style={{padding: '12px', textAlign: 'center', maxWidth: '200px', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>{siparis.notlar}</td>
                                <td style={{padding: '12px', textAlign: 'center'}}>{(siparis.toplam_bedel || 0).toFixed(2)} ₺</td>
                                <td style={{padding: '12px', textAlign: 'center'}}>
                                    <button 
                                        onClick={() => handleCikisYap(siparis.id)}
                                        style={{width: '100px', height: '27px', background: '#F8F8F8', borderRadius: '8px', cursor: 'pointer', border: '1px solid #ccc', fontFamily: 'poppins', fontSize: '12px', color: '#333'}}
                                    >
                                        Listeden Çıkar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    Bekleyen kargo bulunmuyor.
                </div>
            )}
    </div>
    );
}

export default BekleyenKargolar;